import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { PhysicianSelect } from "../../Select";
import getInlineEditPopupContainer from "@tailormed/common-client/component/inline-edit/getInlineEditPopupContainer";

const PatientPhysicianInlineEditor = ({ value, onCommit, onCancel, autoFocus = true, placeholder }) => {
  const { t } = useTranslation();
  const initialRef = useRef(value || null);
  const [currentValue, setCurrentValue] = useState(initialRef.current);
  const committedRef = useRef(false);

  const commitOnce = (newValue) => {
    if (committedRef.current) return;
    committedRef.current = true;
    if (typeof onCommit === "function") onCommit(newValue);
  };

  const cancelOnce = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    if (typeof onCancel === "function") onCancel();
  };

  const handleChange = (newValue) => {
    const next = newValue || null;
    setCurrentValue(next);
    if (next != null) commitOnce(next);
  };

  const handleBlur = () => {
    if (committedRef.current) return;
    // If the user cleared the field and then blurred, persist the clear; otherwise cancel.
    if (currentValue !== initialRef.current) {
      commitOnce(currentValue);
    } else {
      cancelOnce();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancelOnce();
    }
  };

  return (
    <PhysicianSelect
      value={currentValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoFocus={autoFocus}
      defaultOpen={autoFocus}
      placeholder={placeholder || t("inline_edit.physician_placeholder")}
      bordered
      style={{ width: "100%", minWidth: 180 }}
      dropdownStyle={{ minWidth: 280 }}
      listHeight={180}
      getPopupContainer={getInlineEditPopupContainer}
    />
  );
};

PatientPhysicianInlineEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCommit: PropTypes.func,
  onCancel: PropTypes.func,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.node
};

export default PatientPhysicianInlineEditor;
